import React, { useEffect, useRef, useState } from "react";
import styles from "./BottomSheet.module.css";
import { useBackButton } from "../../hooks/useBackButton";
interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  showBackButton?: boolean;
  title?: string;
  children: React.ReactNode;
  height?: "auto" | "half" | "full" | "webcam";
  noAnimation?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  onBack,
  title,
  children,
  height = "auto",
  noAnimation = false,
}) => {
  useBackButton({
    isOpen,
    onBack: onBack || onClose,
  });
  const sheetRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  // Состояния для свайпа
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleBackClick = () => {
    console.log("handleBackClick вызван, onBack:", !!onBack);
    if (onBack) {
      console.log("Вызываем onBack");
      onBack();
    } else {
      console.log("Вызываем onClose");
      onClose();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation(); // Останавливаем всплытие
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
    setIsDragging(true);

    // Отключаем анимацию для плавного движения
    if (sheetRef.current) {
      sheetRef.current.style.transition = "none";
    }
    if (backButtonRef.current) {
      backButtonRef.current.style.transition = "none";
    }
  };

  // Движение пальца - только если начали с челки
  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;

    e.preventDefault(); // Предотвращаем скролл страницы

    const touchY = e.touches[0].clientY;
    const deltaY = touchY - startY;

    setCurrentY(touchY);

    // Движение только вниз и плавно
    if (deltaY > 0) {
      if (sheetRef.current) {
        sheetRef.current.style.transform = `translateY(${deltaY}px)`;
      }
      if (backButtonRef.current) {
        backButtonRef.current.style.transform = `translateY(${deltaY}px)`;
      }
    }
  };

  // Конец свайпа
  const handleTouchEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);

    // Включаем анимацию обратно
    if (sheetRef.current) {
      sheetRef.current.style.transition = "transform 0.3s ease-out";
    }
    if (backButtonRef.current) {
      backButtonRef.current.style.transition = "transform 0.3s ease-out";
    }

    const deltaY = currentY - startY;

    // Если свайп больше 120px - закрываем
    if (deltaY > 120) {
      onClose();
    } else {
      // Возвращаем на место плавно
      if (sheetRef.current) {
        sheetRef.current.style.transform = "translateY(0)";
      }
      if (backButtonRef.current) {
        backButtonRef.current.style.transform = "translateY(0)";
      }
    }

    // Сбрасываем состояния
    setStartY(0);
    setCurrentY(0);
  };

  // Глобальные обработчики - только если свайп активен
  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, startY, currentY]);

  if (!isOpen) return null;

  return (
    <>
      {/* Кнопка назад */}
      <button
        ref={backButtonRef}
        className={styles.backButton}
        onClick={(e) => {
          e.stopPropagation();
          handleBackClick();
        }}
      >
        {onBack ? (
          // Стрелка для возврата назад
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M12 19L5 12L12 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          // Крест для закрытия
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {/* Затемнение */}
      <div className={styles.overlay} onClick={onClose}>
        {/* Модальное окно */}
        <div
          ref={sheetRef}
          className={`${styles.sheet} ${styles[height]} ${
            noAnimation ? styles.noAnimation : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ЧЕЛКА для свайпа - только тут можно свайпать */}
          <div
            ref={handleRef}
            className={styles.swipeHandle}
            onTouchStart={handleTouchStart}
          >
            <div className={styles.swipeIndicator} />
          </div>

          {/* Заголовок если есть */}
          {title && (
            <div className={styles.header}>
              <h3 className={styles.title}>{title}</h3>
              <button className={styles.closeButton} onClick={onClose}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Контент - тут можно спокойно скроллить */}
          <div className={styles.content}>{children}</div>
        </div>
      </div>
    </>
  );
};
